"use client"

import { ProductCategory } from "@medusajs/medusa"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"

import { useClientSessionId } from "@hooks/useClientSessionId"
import { isRoute } from "@lib/routes"
import { retrieveUTMParams } from "@lib/util/utm-params"
import ExternalLink from "@modules/common/components/external-link"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import TopBanner from "@modules/layout/components/top-banner"
import { WebCategoriesButton } from "@modules/navigation/components/web-categories-button"
import { CancelIcon } from "@modules/navigation/icons/CancelIcon"
import { HamburgerIcon } from "@modules/navigation/icons/hamburger"
import { MobileSearch } from "@modules/nelo-search/components/mobile-search"
import WebSearchBar from "@modules/nelo-search/components/search-bar"

import { MobileCategoriesButton } from "../mobile-categories-button"
import MobileCategoriesContent from "../mobile-categories-content"
import { MobileSearchBar } from "../mobile-search-bar"
import { useScrollY } from "../navbar/useScrollY"

type NavbarProps = {
  categories: ProductCategory[]
  cartButton: JSX.Element
  rating: number
  showTopBanner: boolean
}
export default function Navbar(props: NavbarProps) {
  const { scrollY } = useScrollY()
  const pathname = usePathname()

  const isCategoryPage = isRoute.category(pathname)

  return (
    <div
      className={twMerge("z-50 bg-white shadow-none transition-shadow", isCategoryPage ? "" : scrollY !== 0 && "sticky top-0 shadow-2xl")}
    >
      {props.showTopBanner && <TopBanner rating={props.rating} />}

      <header className="relative mx-auto h-16 bg-white text-black duration-200 md:h-20">
        <NavbarContent {...props} />
      </header>
    </div>
  )
}

const NavbarContent = ({ cartButton, categories }: NavbarProps) => {
  /*
    Used on mobile only, navbar has 2 views search and default where "navbarView" indicates navbar should be either search or default view
  */
  const [navbarView, setNavbarView] = useState<"default" | "search">("default")
  const [showMobileCategories, toggleMobileCategories] = useState(false)
  const { sessionId, isMounted } = useClientSessionId()
  const pathname = usePathname()

  useEffect(() => {
    toggleMobileCategories(false)
  }, [pathname])

  const dismissSearchInput = () => {
    setNavbarView("default")
  }

  if (navbarView === "default") {
    return (
      <nav className="content-container text-small-regular txt-xsmall-plus flex h-full w-full items-center gap-3 text-ui-fg-subtle lg:justify-between lg:gap-6">
        {/* Left section: Logo and Categories */}
        <LocalizedClientLink href="/" className="txt-compact-xlarge-plus uppercase hover:text-ui-fg-base" data-testid="nav-store-link">
          <Image
            src="/images/wordmark-blue.svg"
            className="relative -top-[2px] h-7 object-contain object-left md:-top-[8px] md:h-[40px] md:w-[91px]"
            priority
            height={32}
            width={128}
            alt="Nelo"
          />
        </LocalizedClientLink>

        {/* Web */}
        <WebCategoriesButton categories={categories} />
        {/* Center section: Search Bar */}
        <div className="hidden flex-1 justify-center px-6 lg:flex">
          <WebSearchBar />
        </div>

        {/* Mobile */}
        <MobileSearchBar onClick={() => setNavbarView("search")} />
        <MobileCategoriesButton onClick={() => toggleMobileCategories((prev) => !prev)}>
          {showMobileCategories ? <CancelIcon className="w-6" /> : <HamburgerIcon className="w-6" />}
        </MobileCategoriesButton>
        {showMobileCategories && <MobileCategoriesContent categories={categories} />}

        {/* Right section: Registration and Cart */}
        <div className="hidden items-center space-x-6 lg:flex">
          {isMounted && (
            <ExternalLink
              className="font-manrope text-[14px] text-black"
              href={`${process.env.NEXT_PUBLIC_SIGNUP_ORIGIN}/signup/phone-email?${new URLSearchParams({
                source: "marketplace",
                storefrontSessionId: sessionId,
                ...retrieveUTMParams(),
              }).toString()}`}
              data-testid="nav-account-link"
            >
              Regístrate
            </ExternalLink>
          )}
          {cartButton}
        </div>
      </nav>
    )
  }

  return (
    <nav className="content-container flex h-[64px] gap-[14px] py-[15px]">
      <MobileSearch dismissSearchInput={dismissSearchInput} />
      <button onClick={dismissSearchInput}>
        <CancelIcon />
      </button>
    </nav>
  )
}
